import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './RequestsList.module.scss';

import {
  NoDataTableRow,
  Pagination,
  SearchField,
  SummaryHeaderWrapper,
  SummaryPageHeader,
  SummaryTableControls,
  SummaryWrapper,
  TableHeadComponent,
  TableWrapper
} from 'components/SummaryComponents';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import { StyledButton } from 'components/Buttons';
import StyledLink from 'components/StyledLink';
import { FilterButton, FilterChips, FilterWrapper } from 'components/FilterComponentsV2';
import TruncatedText from 'components/TruncatedText';

import { Table, TableBody } from '@material-ui/core';

import {
  useMaintenanceRequestActions,
  useMaintenanceRequestSelector
} from 'hooks/MaintenanceRequest';
import { useCommonActions, useCommonSelector } from 'hooks/Common';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { useUserConfig } from 'hooks/useUserConfig';
import useScrollManager from 'hooks/useScrollManager';
import { useHistory } from 'react-router-dom';

import {
  getChangedByApplyFilter,
  getChangedByLimitFilter,
  getChangedByPageFilter,
  getChangedBySearchFilter,
  getChangedBySortFilter
} from 'helpers/SummaryPagesHelpers';
import { dateTimeFormat, formatDate, getUserFullName } from 'helpers/AppHelpers';

import {
  MAINTENANCE_REQUEST_DETAILS_PATH,
  MAINTENANCE_REQUEST_EDIT_PATH
} from 'constants/routeConstants';
import { MAINTENANCE_REQUESTS_TABLE_PARAMETER } from 'constants/configTableConstants';

import {
  generateChips,
  generateInitialQuery,
  parseQuery,
  removeChip,
  tableConfig
} from './helpers';
import FilterModal from './Filter';
import { FORM_FIELDS } from '../RequestEdit/helpers';

export function RequestsList() {
  const isMobile = useMobileViewport();
  const history = useHistory();
  const tableRef = useRef(null);

  const [tableState, setTableState] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [chips, setChips] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState([]);

  const { isConfigReceived, getTableLimit, isAllLimitEnabled } = useUserConfig();
  const { loadMoreFlag } = useCommonSelector();
  const { setLoadMoreFlagAction } = useCommonActions();

  const { filter } = useMaintenanceRequestSelector();
  const { getRequestsListAction, setFilterAction, getCreationFromAction, clearStateAction } =
    useMaintenanceRequestActions();

  const { filters, pagination, sortRules, responseReceived } = filter;

  const { saveScrollPosition, removeScrollPosition } = useScrollManager({
    tableKey: MAINTENANCE_REQUESTS_TABLE_PARAMETER,
    isDataLoaded: responseReceived
  });

  const getRequests = useCallback(
    (query, isAutoload) => {
      setFilterAction(query);

      getRequestsListAction(parseQuery(query)).then((tableData) => {
        setRequests(tableData, query, isAutoload);
        !loadMoreFlag && setLoadMoreFlagAction(false);
      });
    },
    [setFilterAction, getRequestsListAction]
  );

  const setRequests = useCallback(
    (data, query, isAutoload) => {
      setTableState((prevState) =>
        isAutoload ? prevState.concat(data?.items || []) : data?.items || []
      );

      const updatedFilter = {
        ...query,
        pagination: { limit: data.limit, page: data.pageNumber, totalPages: data.totalPages },
        responseReceived: true
      };
      setFilterAction(updatedFilter);
    },
    [setFilterAction]
  );

  useEffect(() => {
    if (!isConfigReceived) return;

    getCreationFromAction().then((criteria) => {
      setFilterCriteria(criteria);

      const q = {
        tableParam: MAINTENANCE_REQUESTS_TABLE_PARAMETER,
        getTableLimit,
        isAllLimitEnabled,
        filter
      };
      const resultFilter = generateInitialQuery(q);
      getRequests(resultFilter);
      setChips(generateChips(criteria, resultFilter.filters));
    });
  }, [isConfigReceived]);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const findMatches = useCallback(
    (value) => getRequests(getChangedBySearchFilter(filter, value)),
    [filter, getRequests]
  );
  const sortTable = useCallback(
    (rowName) => getRequests(getChangedBySortFilter(filter, rowName)),
    [filter, getRequests]
  );
  const applyFilter = useCallback(
    (values) => {
      getRequests(getChangedByApplyFilter(filter, values));
      setChips(generateChips(filterCriteria, values));
    },
    [filter, filterCriteria, getRequests]
  );
  const changePage = useCallback(
    (page, param) => getRequests(getChangedByPageFilter(filter, page), param === 'AUTOLOAD'),
    [filter, getRequests]
  );
  const changeLimit = useCallback(
    () =>
      getRequests(
        getChangedByLimitFilter(filter, getTableLimit(MAINTENANCE_REQUESTS_TABLE_PARAMETER))
      ),
    [filter, getTableLimit, getRequests]
  );
  const deleteChip = useCallback(
    (chip) => applyFilter(removeChip(filters, chip, filterCriteria)),
    [applyFilter, filters, filterCriteria]
  );

  const createRequest = useCallback(() => {
    removeScrollPosition();
    history.push(MAINTENANCE_REQUEST_EDIT_PATH);
  }, [history]);

  const openFilterModal = useCallback(() => setOpenFilter(true), [setOpenFilter]);

  const MainButton = () => (
    <StyledButton label="Create request" onClick={createRequest} classes={styles.main_button} />
  );

  return (
    <SummaryWrapper>
      <SummaryHeaderWrapper>
        <SummaryPageHeader title={isMobile ? 'Requests' : 'Maintenance Requests'}>
          {!isMobile && <MainButton />}
        </SummaryPageHeader>

        <SummaryTableControls>
          <FilterWrapper>
            <FilterButton onClick={openFilterModal} isActive={openFilter || !!chips?.length} />
            {!isMobile && <FilterChips chips={chips} onDelete={deleteChip} />}
          </FilterWrapper>
          <SearchField filterValue={filters?.searchQuery} onSearch={findMatches} />
        </SummaryTableControls>
      </SummaryHeaderWrapper>

      {isMobile && <MainButton />}

      <TableWrapper ref={tableRef} enableHorizontalScroll>
        <Table>
          <TableHeadComponent config={tableConfig} sortRules={sortRules} onSortApply={sortTable} />
          <TableBody>
            {tableState?.length ? (
              tableState.map((row) => (
                <StyledTableRow key={row.id} className={styles.table_row}>
                  <StyledTableCell className={styles.idCell}>
                    <StyledLink
                      bold
                      onClick={saveScrollPosition}
                      to={`${MAINTENANCE_REQUEST_DETAILS_PATH}/${row.id}`}>
                      {row.id}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={styles.jobNumberCell}>
                    <TruncatedText>{row[FORM_FIELDS.JOB_NUMBER] || ''}</TruncatedText>
                  </StyledTableCell>
                  <StyledTableCell className={styles.jobLocationCell}>
                    <TruncatedText>{row[FORM_FIELDS.JOB_LOCATION] || ''}</TruncatedText>
                  </StyledTableCell>
                  <StyledTableCell className={styles.statusCell}>
                    {row[FORM_FIELDS.STATUS]?.displayName || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.openedOnCell}>
                    {formatDate(row[FORM_FIELDS.OPENED_ON], dateTimeFormat)}
                  </StyledTableCell>
                  <StyledTableCell className={styles.priorityCell}>
                    {row[FORM_FIELDS.PRIORITY]?.displayName || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.severityCell}>
                    {row[FORM_FIELDS.SEVERITY]?.displayName || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.taskRequestCell}>
                    <TruncatedText>{row[FORM_FIELDS.WORK_ORDER_TASK_REQUEST] || ''}</TruncatedText>
                  </StyledTableCell>
                  <StyledTableCell className={styles.taskLocationCell}>
                    <TruncatedText>{row[FORM_FIELDS.TASK_LOCATION] || ''}</TruncatedText>
                  </StyledTableCell>
                  <StyledTableCell className={styles.requesterNameCell}>
                    {row[FORM_FIELDS.REQUESTER_NAME] || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.resolvedOnCell}>
                    {row[FORM_FIELDS.RESOLVED_ON]
                      ? formatDate(row[FORM_FIELDS.RESOLVED_ON], dateTimeFormat)
                      : ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.resolverNameCell}>
                    {row[FORM_FIELDS.RESOLVER] ? getUserFullName(row[FORM_FIELDS.RESOLVER]) : ''}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : responseReceived ? (
              <NoDataTableRow isTableFiltered={!!chips?.length || !!filters?.searchQuery} />
            ) : null}
          </TableBody>
        </Table>
      </TableWrapper>

      <Pagination
        withOptionAll
        rowsNumber={tableState?.length}
        currentPage={pagination?.page}
        totalPages={pagination?.totalPages}
        onChangePage={changePage}
        onChangeLimit={changeLimit}
        selectedLimit={pagination?.limit}
        tableName={MAINTENANCE_REQUESTS_TABLE_PARAMETER}
        tableRef={tableRef}
      />

      {openFilter && (
        <FilterModal
          open={openFilter}
          setOpen={setOpenFilter}
          onApply={applyFilter}
          filter={filter}
          filterCriteria={filterCriteria}
        />
      )}
    </SummaryWrapper>
  );
}
